import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Grid, Button, Chip, LinearProgress,
  Alert, Stack, Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import {
  PlayArrow as StartIcon,
  Refresh as ReplanIcon,
  NightsStay as CloseIcon,
  WbSunny as TomorrowIcon,
  Warning as WarningIcon,
  TrendingUp as StrategyIcon,
  Schedule as ScheduleIcon,
  RocketLaunch as SetupIcon,
  WbTwilight as MiddayIcon,
  CalendarMonth as CalendarIcon,
  Assignment as ItemsIcon,
  LinkOff as UnlinkedIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { rhythmApi, workItemsApi, calendarSourcesApi, calendarApi } from '../api/services';
import type { StartMyDayResponse, ScheduledWorkBlock, CalendarEvent, WorkItem, AnnualStrategicDriver, CapacitySummary, PlanStatus, CloseDayResponse } from '../types/models';
import PriorityChip from '../components/PriorityChip';
import StatusChip from '../components/StatusChip';
import GuidedReflectionDialog from '../components/GuidedReflectionDialog';

// Pre-load summary shown before Start My Day
interface PreLoadSummary {
  meetingCount: number;
  meetingHours: number;
  availableHours: number;
  totalWorkingHours: number;
  workItemCount: number;
  carryForwardCount: number;
  longCarryForwardCount: number; // carried 4+ days
  decisionsPending: number;
  overdueFollowUps: number;
  inProgressCount: number;
  upcomingMeetings: CalendarEvent[];
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const [dayData, setDayData] = useState<StartMyDayResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSetupPrompt, setShowSetupPrompt] = useState(false);
  const [preLoad, setPreLoad] = useState<PreLoadSummary | null>(null);
  const [preLoadLoading, setPreLoadLoading] = useState(true);
  const [closingDay, setClosingDay] = useState(false);
  const [showReflectionDialog, setShowReflectionDialog] = useState(false);
  const [closeDayResult, setCloseDayResult] = useState<CloseDayResponse | null>(null);
  const [showCloseDaySummary, setShowCloseDaySummary] = useState(false);
  const today = format(new Date(), 'yyyy-MM-dd');

  // Pre-load summary: fetch lightweight data on mount
  useEffect(() => {
    let cancelled = false;
    const loadPreSummary = async () => {
      setPreLoadLoading(true);
      try {
        const [todayItems, calendarEvents, inProgressItems] = await Promise.all([
          workItemsApi.getByDate(today),
          calendarApi.getByDate(today).catch(() => [] as CalendarEvent[]),
          workItemsApi.getByStatus('InProgress'),
        ]);

        if (cancelled) return;

        const now = new Date();
        const meetings = calendarEvents.filter(e => !e.isAllDay);
        const meetingMinutes = meetings.reduce((sum, e) => {
          const start = new Date(e.startTime);
          const end = new Date(e.endTime);
          return sum + Math.max(0, (end.getTime() - start.getTime()) / 60000);
        }, 0);
        const totalWorkingHours = 8; // default working day
        const meetingHours = Math.round(meetingMinutes / 60 * 10) / 10;
        const availableHours = Math.max(0, Math.round((totalWorkingHours - meetingHours) * 10) / 10);

        // Find meetings starting in the next 2 hours for prep prompts
        const upcomingMeetings = meetings.filter(e => {
          const start = new Date(e.startTime);
          const hoursUntil = (start.getTime() - now.getTime()) / 3600000;
          return hoursUntil > 0 && hoursUntil <= 2;
        });

        const carryItems = todayItems.filter(i => i.isCarryForward);
        const longCarry = carryItems.filter(i => {
          if (!i.originalDate) return false;
          const days = Math.ceil((now.getTime() - new Date(i.originalDate).getTime()) / 86400000);
          return days >= 4;
        });

        const decisionsPending = todayItems.filter(i =>
          i.type === 'Decision' && !['Done', 'Cancelled'].includes(i.status)
        ).length;

        const overdueFollowUps = todayItems.filter(i =>
          i.type === 'FollowUp' && !['Done', 'Cancelled'].includes(i.status) &&
          i.targetDate && i.targetDate < today
        ).length;

        setPreLoad({
          meetingCount: meetings.length,
          meetingHours,
          availableHours,
          totalWorkingHours,
          workItemCount: todayItems.length,
          carryForwardCount: carryItems.length,
          longCarryForwardCount: longCarry.length,
          decisionsPending,
          overdueFollowUps,
          inProgressCount: inProgressItems.length,
          upcomingMeetings,
        });
      } catch {
        // Non-critical — just don't show the pre-load summary
      } finally {
        if (!cancelled) setPreLoadLoading(false);
      }
    };
    loadPreSummary();
    return () => { cancelled = true; };
  }, [today]);

  // Check if user is new (no work items, no calendar sources)
  useEffect(() => {
    let cancelled = false;
    const checkNewUser = async () => {
      try {
        const [items, sources] = await Promise.all([
          workItemsApi.getAll(),
          calendarSourcesApi.getAll(),
        ]);
        if (!cancelled && items.length === 0 && sources.length === 0) {
          setShowSetupPrompt(true);
        }
      } catch {
        // Ignore errors — just don't show the prompt
      }
    };
    checkNewUser();
    return () => { cancelled = true; };
  }, []);

  const loadDay = useCallback(async (action: 'start' | 'replan' = 'start') => {
    setLoading(true);
    setError(null);
    try {
      const data = action === 'start'
        ? await rhythmApi.startMyDay()
        : await rhythmApi.replan();
      setDayData(data);
      setShowSetupPrompt(false);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load day');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleCloseDay = useCallback(async () => {
    setClosingDay(true);
    setError(null);
    try {
      const result = await rhythmApi.closeDay({ date: today });
      setCloseDayResult(result);
      setShowCloseDaySummary(true);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to close day');
    } finally {
      setClosingDay(false);
    }
  }, [today]);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4">
            {format(new Date(), 'EEEE, d MMMM yyyy')}
          </Typography>
          {dayData?.plan.dailyTheme && (
            <Typography variant="subtitle1" sx={{ mt: 0.5 }}>
              Theme: {dayData.plan.dailyTheme}
            </Typography>
          )}
        </Box>
        <Stack direction="row" spacing={1}>
          <Button variant="contained" startIcon={<StartIcon />} onClick={() => loadDay('start')} disabled={loading}>
            Start My Day
          </Button>
          <Button variant="outlined" startIcon={<ReplanIcon />} onClick={() => loadDay('replan')} disabled={loading || !dayData}>
            Replan
          </Button>
          <Button variant="outlined" startIcon={<MiddayIcon />} onClick={() => loadDay('replan')} disabled={loading || !dayData}>
            Midday Reset
          </Button>
          <Button variant="outlined" startIcon={<CloseIcon />} onClick={handleCloseDay} disabled={loading || closingDay || !dayData}>
            Close Day
          </Button>
          <Button variant="outlined" startIcon={<TomorrowIcon />} onClick={async () => {
            setLoading(true);
            try {
              const data = await rhythmApi.planTomorrow();
              setDayData(data);
            } catch (err: any) {
              setError(err.message);
            } finally {
              setLoading(false);
            }
          }} disabled={loading}>
            Plan Tomorrow
          </Button>
        </Stack>
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Setup prompt for new users */}
      {showSetupPrompt && !dayData && !loading && (
        <Card sx={{ mb: 3, borderLeft: 3, borderColor: 'primary.main' }}>
          <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2.5, py: 2.5 }}>
            <SetupIcon sx={{ fontSize: 40, color: 'primary.main' }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ mb: 0.5 }}>Welcome to Cadence!</Typography>
              <Typography color="text.secondary">
                Complete your setup to get started. Connect your calendar, set your working hours, and optionally invite your EA.
              </Typography>
            </Box>
            <Button variant="contained" onClick={() => navigate('/getting-started')} sx={{ whiteSpace: 'nowrap' }}>
              Complete Setup
            </Button>
          </CardContent>
        </Card>
      )}

      {!dayData && !loading && (
        <>
          {/* Pre-load summary */}
          {preLoad && !preLoadLoading && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Stack spacing={1.5}>
                  {/* Meetings & focus line */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CalendarIcon sx={{ fontSize: 18, color: 'info.main' }} />
                    <Typography variant="body1">
                      <strong>Today:</strong> {preLoad.meetingCount} meeting{preLoad.meetingCount !== 1 ? 's' : ''} ({preLoad.meetingHours} hrs)
                      {' '}&middot;{' '}
                      {preLoad.availableHours} hours available for focus
                    </Typography>
                  </Box>

                  {/* Work items line */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ItemsIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                    <Typography variant="body1">
                      <strong>{preLoad.workItemCount}</strong> work items planned
                      {preLoad.carryForwardCount > 0 && (
                        <> &middot; <span style={{ color: preLoad.longCarryForwardCount > 0 ? '#f44336' : '#ff9800' }}>
                          {preLoad.carryForwardCount} carried forward
                          {preLoad.longCarryForwardCount > 0 && ` (${preLoad.longCarryForwardCount} for 4+ days)`}
                        </span></>
                      )}
                    </Typography>
                  </Box>

                  {/* Decisions & follow-ups */}
                  {(preLoad.decisionsPending > 0 || preLoad.overdueFollowUps > 0 || preLoad.inProgressCount > 0) && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <WarningIcon sx={{ fontSize: 18, color: 'warning.main' }} />
                      <Typography variant="body1">
                        {[
                          preLoad.decisionsPending > 0 && `${preLoad.decisionsPending} decision${preLoad.decisionsPending !== 1 ? 's' : ''} pending`,
                          preLoad.overdueFollowUps > 0 && `${preLoad.overdueFollowUps} overdue follow-up${preLoad.overdueFollowUps !== 1 ? 's' : ''}`,
                          preLoad.inProgressCount > 0 && `${preLoad.inProgressCount} in progress`,
                        ].filter(Boolean).join(' \u00B7 ')}
                      </Typography>
                    </Box>
                  )}

                  {/* Meeting prep prompts */}
                  {preLoad.upcomingMeetings.length > 0 && (
                    <Alert severity="info" sx={{ mt: 1 }}>
                      <Typography variant="body2" fontWeight={600}>
                        {preLoad.upcomingMeetings.length} meeting{preLoad.upcomingMeetings.length !== 1 ? 's' : ''} in the next 2 hours
                      </Typography>
                      {preLoad.upcomingMeetings.map(m => (
                        <Typography key={m.id} variant="body2" color="text.secondary">
                          {format(new Date(m.startTime), 'HH:mm')} — {m.title}
                          {m.strategicDriverId ? ' \ud83d\udcce Strategy linked' : ''}
                        </Typography>
                      ))}
                    </Alert>
                  )}

                  {/* Time pressure indicator */}
                  <Box sx={{ mt: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        Meeting load
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {preLoad.availableHours}h available for focused work
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(100, preLoad.totalWorkingHours > 0 ? (preLoad.meetingHours / preLoad.totalWorkingHours) * 100 : 0)}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        bgcolor: 'rgba(255,255,255,0.06)',
                        '& .MuiLinearProgress-bar': {
                          borderRadius: 4,
                          bgcolor: preLoad.meetingHours / preLoad.totalWorkingHours > 0.7
                            ? 'error.main'
                            : preLoad.meetingHours / preLoad.totalWorkingHours > 0.5
                              ? 'warning.main'
                              : 'success.main',
                        },
                      }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25, display: 'block' }}>
                      Today: {preLoad.meetingHours} hrs meetings &middot; {Math.round((preLoad.meetingHours / preLoad.totalWorkingHours) * 100)}% of day
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          )}

          <Card sx={{ p: 6, textAlign: 'center' }}>
            <Typography variant="h5" gutterBottom>Ready to begin</Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              Click "Start My Day" to generate your AI-assisted plan, sync your calendar, and build your schedule.
            </Typography>
            <Button variant="contained" size="large" startIcon={<StartIcon />} onClick={() => loadDay('start')}>
              Start My Day
            </Button>
          </Card>
        </>
      )}

      {closingDay && <LinearProgress sx={{ mb: 2 }} />}

      {/* Close Day Summary Dialog */}
      <CloseDaySummaryDialog
        open={showCloseDaySummary}
        result={closeDayResult}
        onReflect={() => {
          setShowCloseDaySummary(false);
          setShowReflectionDialog(true);
        }}
        onClose={() => setShowCloseDaySummary(false)}
      />

      {/* Guided Reflection Dialog (triggered after Close Day) */}
      <GuidedReflectionDialog
        open={showReflectionDialog}
        onClose={() => setShowReflectionDialog(false)}
        onSaved={() => setShowReflectionDialog(false)}
        date={today}
        trigger="CloseDay"
      />

      {dayData && (
        <Grid container spacing={2.5}>
          {/* Capacity Summary */}
          <Grid size={{ xs: 12, md: 4 }}>
            <CapacityCard capacity={dayData.capacity} />
          </Grid>

          {/* Plan Status */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>Plan Status</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <StatusChip status={dayData.plan.status} />
                  {dayData.plan.isOverloaded && (
                    <Chip icon={<WarningIcon />} label="Overloaded" size="small" color="error" variant="outlined" />
                  )}
                </Box>
                <PlanStatusTimeline currentStatus={dayData.plan.status} />
                {dayData.warnings.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    {dayData.warnings.map((w, i) => (
                      <Alert key={i} severity="warning" sx={{ mb: 1, py: 0 }}>{w}</Alert>
                    ))}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Strategic Drivers */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  <StrategyIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom' }} />
                  Annual Strategy
                </Typography>
                {dayData.strategicDrivers.map((driver) => (
                  <Box key={driver.id} sx={{ mb: 1.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2" fontWeight={600}>{driver.title}</Typography>
                      <Typography variant="caption" color="text.secondary">{driver.progressPercentage}%</Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={driver.progressPercentage}
                      sx={{ height: 4, borderRadius: 2 }}
                    />
                  </Box>
                ))}
                {dayData.strategicDrivers.length === 0 && (
                  <Typography variant="body2" color="text.secondary">No strategic drivers configured</Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* AI Briefing */}
          {dayData.aiBriefing && (
            <Grid size={{ xs: 12 }}>
              <Card sx={{ borderLeft: 3, borderColor: 'primary.main' }}>
                <CardContent>
                  <Typography variant="subtitle2" color="primary.main" gutterBottom>AI Briefing</Typography>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>{dayData.aiBriefing}</Typography>
                  {dayData.aiRisks && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" color="warning.main" gutterBottom>Risks</Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>{dayData.aiRisks}</Typography>
                    </Box>
                  )}
                  {dayData.aiStrategyAlignment && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" color="success.main" gutterBottom>Strategy Alignment</Typography>
                      <Typography variant="body2">{dayData.aiStrategyAlignment}</Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Strategic Alignment Score */}
          <Grid size={{ xs: 12 }}>
            <StrategicAlignmentCard dayData={dayData} />
          </Grid>

          {/* Scheduled Timeline */}
          <Grid size={{ xs: 12, lg: 7 }}>
            <Card>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  <ScheduleIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom' }} />
                  Scheduled Timeline
                </Typography>
                <TimelineView blocks={dayData.scheduledBlocks} events={dayData.calendarEvents} />
              </CardContent>
            </Card>
          </Grid>

          {/* Overflow & Carry Forward */}
          <Grid size={{ xs: 12, lg: 5 }}>
            <Stack spacing={2.5}>
              {dayData.overflowItems.length > 0 && (
                <Card sx={{ borderLeft: 3, borderColor: 'warning.main' }}>
                  <CardContent>
                    <Typography variant="subtitle2" color="warning.main" gutterBottom>
                      Overflow Items ({dayData.overflowItems.length})
                    </Typography>
                    {dayData.overflowItems.map((item) => (
                      <WorkItemRow key={item.id} item={item} />
                    ))}
                  </CardContent>
                </Card>
              )}
              {dayData.carryForwardItems.length > 0 && (
                <Card sx={{ borderLeft: 3, borderColor: 'error.main' }}>
                  <CardContent>
                    <Typography variant="subtitle2" color="error.main" gutterBottom>
                      Carry Forward ({dayData.carryForwardItems.length})
                    </Typography>
                    {dayData.carryForwardItems.map((item) => (
                      <WorkItemRow key={item.id} item={item} />
                    ))}
                  </CardContent>
                </Card>
              )}
              {dayData.carriedForwardFromYesterday.length > 0 && (
                <Card sx={{ borderLeft: 3, borderColor: 'secondary.main' }}>
                  <CardContent>
                    <Typography variant="subtitle2" color="secondary.main" gutterBottom>
                      Carried From Yesterday ({dayData.carriedForwardFromYesterday.length})
                    </Typography>
                    {dayData.carriedForwardFromYesterday.map((item) => (
                      <WorkItemRow key={item.id} item={item} />
                    ))}
                  </CardContent>
                </Card>
              )}
            </Stack>
          </Grid>
        </Grid>
      )}
    </Box>
  );
}

function CapacityCard({ capacity }: { capacity: CapacitySummary }) {
  const totalMinutes = capacity.availableMinutes + capacity.meetingMinutes;
  const focusMinutes = Math.max(capacity.availableMinutes - capacity.plannedMinutes, 0);
  const meetingPct = totalMinutes > 0 ? (capacity.meetingMinutes / totalMinutes) * 100 : 0;
  const plannedPct = totalMinutes > 0 ? (capacity.plannedMinutes / totalMinutes) * 100 : 0;
  const focusPct = totalMinutes > 0 ? (focusMinutes / totalMinutes) * 100 : 0;

  // Donut ring data
  const segments = [
    { label: 'Focus', pct: focusPct, color: '#66BB6A' },
    { label: 'Meetings', pct: meetingPct, color: '#FF8A65' },
    { label: 'Planned', pct: plannedPct, color: '#6C9BFF' },
  ];

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>Capacity</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 2 }}>
          <StatBox label="Available" value={`${Math.round(capacity.availableMinutes / 60 * 10) / 10}h`} />
          <StatBox label="Meetings" value={`${Math.round(capacity.meetingMinutes / 60 * 10) / 10}h`} />
          <StatBox label="Planned" value={`${Math.round(capacity.plannedMinutes / 60 * 10) / 10}h`} />
          <StatBox label="Overflow" value={`${Math.round(capacity.overflowMinutes)}m`} color={capacity.isOverloaded ? 'error.main' : undefined} />
        </Box>

        {/* Segmented capacity bar */}
        <Typography variant="caption" color="text.secondary" gutterBottom>Breakdown</Typography>
        <Box sx={{ display: 'flex', height: 8, borderRadius: 4, overflow: 'hidden', bgcolor: 'rgba(255,255,255,0.06)', mb: 1 }}>
          {segments.map((seg) => (
            seg.pct > 0 && (
              <Box key={seg.label} sx={{ width: `${seg.pct}%`, bgcolor: seg.color, transition: 'width 0.3s ease' }} />
            )
          ))}
        </Box>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
          {segments.map((seg) => (
            <Box key={seg.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: seg.color }} />
              <Typography variant="caption" color="text.secondary">{seg.label} {Math.round(seg.pct)}%</Typography>
            </Box>
          ))}
        </Box>

        {/* Mini donut ring */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <DonutRing segments={segments} size={64} />
          <Box>
            <Typography variant="caption" color="text.secondary">Utilisation</Typography>
            <Typography variant="h6" sx={{ color: capacity.utilisationPercent > 85 ? 'error.main' : capacity.utilisationPercent > 70 ? 'warning.main' : 'primary.main' }}>
              {Math.round(capacity.utilisationPercent)}%
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

function DonutRing({ segments, size = 64 }: { segments: { label: string; pct: number; color: string }[]; size?: number }) {
  const radius = (size - 8) / 2;
  const circumference = 2 * Math.PI * radius;
  let offset = 0;

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth={6} />
      {segments.map((seg) => {
        const dashLength = (seg.pct / 100) * circumference;
        const currentOffset = offset;
        offset += dashLength;
        return dashLength > 0 ? (
          <circle
            key={seg.label}
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={seg.color}
            strokeWidth={6}
            strokeDasharray={`${dashLength} ${circumference - dashLength}`}
            strokeDashoffset={-currentOffset}
            strokeLinecap="round"
            style={{ transform: 'rotate(-90deg)', transformOrigin: '50% 50%' }}
          />
        ) : null;
      })}
    </svg>
  );
}

const PLAN_STEPS: PlanStatus[] = ['Draft', 'Proposed', 'Approved', 'Active', 'Closed'];

function PlanStatusTimeline({ currentStatus }: { currentStatus: PlanStatus }) {
  const currentIndex = PLAN_STEPS.indexOf(currentStatus);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0, mt: 1 }}>
      {PLAN_STEPS.map((step, i) => {
        const isActive = i === currentIndex;
        const isPast = i < currentIndex;
        return (
          <React.Fragment key={step}>
            {i > 0 && (
              <Box sx={{
                flex: 1, height: 2, mx: 0.25,
                bgcolor: isPast || isActive ? 'primary.main' : 'rgba(255,255,255,0.1)',
                transition: 'background-color 0.3s ease',
              }} />
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 36 }}>
              <Box sx={{
                width: isActive ? 12 : 8,
                height: isActive ? 12 : 8,
                borderRadius: '50%',
                bgcolor: isActive ? 'primary.main' : isPast ? 'primary.main' : 'rgba(255,255,255,0.15)',
                border: isActive ? '2px solid rgba(124,58,237,0.4)' : 'none',
                transition: 'all 0.3s ease',
              }} />
              <Typography variant="caption" sx={{
                fontSize: 9, mt: 0.5, color: isActive ? 'primary.main' : 'text.secondary',
                fontWeight: isActive ? 700 : 400,
              }}>
                {step}
              </Typography>
            </Box>
          </React.Fragment>
        );
      })}
    </Box>
  );
}

function StatBox({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <Box sx={{ p: 1, borderRadius: 1, bgcolor: 'rgba(255,255,255,0.03)' }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="h6" sx={{ color: color || 'text.primary' }}>{value}</Typography>
    </Box>
  );
}

function TimelineView({ blocks, events }: { blocks: ScheduledWorkBlock[]; events: CalendarEvent[] }) {
  const allItems = [
    ...events.filter(e => !e.isAllDay).map(e => ({
      type: 'event' as const,
      start: e.startTime,
      end: e.endTime,
      title: e.title,
      isPrivate: e.isPrivate,
      isPriority: e.isPriorityMeeting,
    })),
    ...blocks.map(b => ({
      type: 'block' as const,
      start: b.startTime,
      end: b.endTime,
      title: b.workItem?.title || 'Work block',
      priority: b.workItem?.priority,
    })),
  ].sort((a, b) => a.start.localeCompare(b.start));

  if (allItems.length === 0) {
    return <Typography color="text.secondary" variant="body2">No scheduled items</Typography>;
  }

  return (
    <Stack spacing={0.5}>
      {allItems.map((item, i) => (
        <Box
          key={i}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            p: 1,
            borderRadius: 1,
            bgcolor: item.type === 'event' ? 'rgba(255, 138, 101, 0.08)' : 'rgba(108, 155, 255, 0.08)',
            borderLeft: 3,
            borderColor: item.type === 'event'
              ? (item.isPriority ? 'error.main' : 'secondary.main')
              : 'primary.main',
          }}
        >
          <Typography variant="caption" sx={{ fontFamily: 'monospace', minWidth: 90, color: 'text.secondary' }}>
            {formatTime(item.start)} – {formatTime(item.end)}
          </Typography>
          <Typography variant="body2" sx={{ flex: 1, fontWeight: 500 }}>
            {item.title}
          </Typography>
          {item.type === 'event' && item.isPrivate && (
            <Chip label="Priority" size="small" color="error" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
          )}
          {item.type === 'block' && (item as any).priority && (
            <PriorityChip priority={(item as any).priority} />
          )}
        </Box>
      ))}
    </Stack>
  );
}

function StrategicAlignmentCard({ dayData }: { dayData: StartMyDayResponse }) {
  // Gather all scheduled work items
  const allItems = [
    ...dayData.scheduledBlocks.map(b => b.workItem).filter(Boolean) as WorkItem[],
    ...dayData.overflowItems,
  ];
  // Deduplicate by id
  const uniqueItems = Array.from(new Map(allItems.map(i => [i.id, i])).values());
  const totalCount = uniqueItems.length;
  if (totalCount === 0) return null;

  const linkedItems = uniqueItems.filter(i => i.strategicDriverId);
  const unlinkedCount = totalCount - linkedItems.length;
  const alignmentPct = Math.round((linkedItems.length / totalCount) * 100);

  // Group linked items by driver
  const driverCounts: Record<string, { title: string; count: number }> = {};
  for (const item of linkedItems) {
    const driverId = item.strategicDriverId!;
    const driverTitle = item.strategicDriver?.title || dayData.strategicDrivers.find(d => d.id === driverId)?.title || 'Unknown';
    if (!driverCounts[driverId]) driverCounts[driverId] = { title: driverTitle, count: 0 };
    driverCounts[driverId].count++;
  }

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <StrategyIcon sx={{ fontSize: 18, color: 'success.main' }} />
          <Typography variant="subtitle2" color="text.secondary">Strategic Alignment</Typography>
          <Chip
            label={`${alignmentPct}% aligned`}
            size="small"
            color={alignmentPct >= 60 ? 'success' : alignmentPct >= 30 ? 'warning' : 'error'}
            variant="outlined"
            sx={{ ml: 1 }}
          />
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {alignmentPct}% of today's items linked to a strategic driver
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {Object.entries(driverCounts).map(([id, { title, count }]) => (
            <Chip
              key={id}
              label={`${title}: ${count} item${count !== 1 ? 's' : ''}`}
              size="small"
              variant="outlined"
              color="primary"
            />
          ))}
          {unlinkedCount > 0 && (
            <Chip
              icon={<UnlinkedIcon sx={{ fontSize: 14 }} />}
              label={`Unlinked: ${unlinkedCount} item${unlinkedCount !== 1 ? 's' : ''}`}
              size="small"
              variant="outlined"
              color="default"
            />
          )}
        </Box>
      </CardContent>
    </Card>
  );
}

function CloseDaySummaryDialog({ open, result, onReflect, onClose }: {
  open: boolean;
  result: CloseDayResponse | null;
  onReflect: () => void;
  onClose: () => void;
}) {
  if (!result) return null;

  const incomplete = result.incompleteItems?.length || 0;
  const totalPlanned = incomplete + (result.plan?.plannedMinutes ? Math.round(result.plan.plannedMinutes / 30) : 0); // approximate
  const carryForwardCount = result.itemsToCarryForward || 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Day Closed</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {incomplete > 0 ? (
            <Alert severity="info">
              {incomplete} item{incomplete !== 1 ? 's' : ''} incomplete.
              {carryForwardCount > 0 && ` ${carryForwardCount} will carry forward to tomorrow.`}
            </Alert>
          ) : (
            <Alert severity="success">
              All planned items completed. Well done!
            </Alert>
          )}

          <Typography variant="subtitle2" color="text.secondary">Reflection prompts</Typography>
          <Stack spacing={1}>
            <Typography variant="body2">
              {incomplete > 0
                ? `You had ${incomplete} incomplete item${incomplete !== 1 ? 's' : ''}. What got in the way?`
                : 'You completed all planned items. What made today successful?'
              }
            </Typography>
            <Typography variant="body2">
              What was your most impactful action today?
            </Typography>
            <Typography variant="body2">
              Is there anything you would do differently tomorrow?
            </Typography>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Skip</Button>
        <Button variant="contained" onClick={onReflect}>
          Start Reflection
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function WorkItemRow({ item }: { item: WorkItem }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.75, borderBottom: 1, borderColor: 'divider' }}>
      <PriorityChip priority={item.priority} />
      <Typography variant="body2" sx={{ flex: 1 }}>{item.title}</Typography>
      <Typography variant="caption" color="text.secondary">{item.estimatedMinutes}m</Typography>
    </Box>
  );
}

function formatTime(iso: string): string {
  try {
    // Handle both ISO datetime and time-only strings
    if (iso.includes('T')) {
      return new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    }
    return iso.substring(0, 5);
  } catch {
    return iso;
  }
}
